import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchConversations, setCurrentConversation, fetchConversationHistory, clearMessages } from '../store/slices/chatSlice';
import './ConversationList.css';

const ConversationList = () => {
  const dispatch = useDispatch();
  const { conversations, conversationsLoading, currentConversationId } = useSelector((state) => state.chat);

  useEffect(() => {
    dispatch(fetchConversations());
  }, [dispatch]);

  const handleConversationClick = (conversationId) => {
    if (conversationId) {
      // Fetch conversation history for the selected conversation
      dispatch(fetchConversationHistory(conversationId));
    } else {
      // Start a new conversation - clear messages and conversation ID
      dispatch(setCurrentConversation(null));
      dispatch(clearMessages());
    }
  };

  const getConversationTitle = (conversation) => {
    if (conversation.title && conversation.title.trim()) {
      return conversation.title;
    }
    if (conversation.last_message && conversation.last_message.content) {
      return truncateText(conversation.last_message.content, 30);
    }
    return 'New conversation';
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return 'Unknown';
    }
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Unknown';
      }
      
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 1) {
        return 'Today';
      } else if (diffDays === 2) {
        return 'Yesterday';
      } else if (diffDays <= 7) {
        return date.toLocaleDateString('en-US', { weekday: 'short' });
      } else {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Unknown';
    }
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text || typeof text !== 'string') {
      return 'New conversation';
    }
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (conversationsLoading) {
    return (
      <div className="conversation-list">
        <div className="conversation-list-header">
          <h3>Conversations</h3>
        </div>
        <div className="conversation-list-loading">
          <div className="loading-spinner"></div>
          <p>Loading conversations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="conversation-list">
      <div className="conversation-list-header">
        <h3>Conversations</h3>
        <button 
          className="new-chat-btn"
          onClick={() => handleConversationClick(null)}
        >
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
          </svg>
          New Chat
        </button>
      </div>
      
      <div className="conversation-list-content">
        {conversations.length === 0 ? (
          <div className="no-conversations">
            <div className="no-conversations-icon">💬</div>
            <p>No conversations yet</p>
            <p>Start a new chat to begin</p>
          </div>
        ) : (
          conversations.map((conversation) => (
            <div
              key={conversation.id || conversation.pk || Date.now()}
              className={`conversation-item ${currentConversationId === (conversation.id || conversation.pk) ? 'active' : ''}`}
              onClick={() => handleConversationClick(conversation.id || conversation.pk)}
            >
              <div className="conversation-content">
                <div className="conversation-title">
                  {getConversationTitle(conversation)}
                </div>
                <div className="conversation-meta">
                  <span className="conversation-date">
                    {formatDate(conversation.created_at || conversation.updated_at || conversation.timestamp)}
                  </span>
                  <div className="conversation-stats">
                    {conversation.unread_count > 0 && (
                      <span className="unread-count">
                        {conversation.unread_count}
                      </span>
                    )}
                    {conversation.last_message && (
                      <span className="message-count">
                        {conversation.last_message.id} messages
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="conversation-actions">
                <button 
                  className="conversation-action-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Handle delete conversation
                  }}
                  title="Delete conversation"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                  </svg>
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ConversationList; 